/**
 * SLDNewWindowPortal — opens SLD viewer in a separate browser window or Electron BrowserWindow.
 * Extracted from SLDViewerModal.tsx (no behavior changes).
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMonitoringStore } from '@/stores';
import type { SLDDiagram } from '@/stores/monitoringStore';

export interface SLDNewWindowPortalProps {
  title: string;
  diagram?: SLDDiagram;
  width?: number;
  height?: number;
  onClose: () => void;
  children: React.ReactNode;
}

export function SLDNewWindowPortal({ title, diagram, width = 1200, height = 800, onClose, children }: SLDNewWindowPortalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const isElectron = !!(window as any).electronAPI?.isElectron;

  // Ref guard: prevents React 18 StrictMode from opening two BrowserWindows
  // (StrictMode mounts -> unmounts -> remounts; without this, two windows open).
  const electronIdRef = useRef<string | null>(null);

  // ── Electron path: open a new BrowserWindow via IPC ──
  // Electron's window.open() routes to the system browser (setWindowOpenHandler),
  // and its WindowProxy doesn't support document access for createPortal.
  // Instead we open a new BrowserWindow that loads its own standalone SLD viewer,
  // with monitoring state passed via localStorage.
  useEffect(() => {
    if (!isElectron || !diagram) return;

    const electronAPI = (window as any).electronAPI;
    let id = electronIdRef.current;

    // Only open the window on first mount (ref survives StrictMode remount)
    if (!id) {
      id = `sld-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      electronIdRef.current = id;

      // Serialize current monitoring state for the child window
      const monState = useMonitoringStore.getState();
      const stateKey = `sld-standalone-${id}`;
      localStorage.setItem(stateKey, JSON.stringify({
        diagram,
        runtimeStates: Object.fromEntries(monState.runtimeStates),
        triggeredRules: monState.triggeredAdvancedRules || [],
        projectConfig: monState.projectConfig,
        schema: monState.schema || null,
      }));

      // Build URL from current location (works for both http://localhost and file:// paths)
      const baseUrl = window.location.href.split('?')[0].split('#')[0];
      electronAPI.openSLDWindow({
        url: `${baseUrl}?sld-standalone=${stateKey}`,
        width, height, title, id,
      });
    }

    // (Re-)attach close listener (cleaned up and re-attached on StrictMode remount)
    const removeListener = electronAPI.onSLDWindowClosed?.((closedId: string) => {
      if (closedId === id) onClose();
    });

    return () => {
      removeListener?.();
      // Don't remove localStorage here — child window reads & removes it on load.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Browser path: window.open() + createPortal ──
  // In browsers, window.open() returns a real Window reference with full document
  // access, so we can use createPortal to render React components into it.
  useEffect(() => {
    if (isElectron) return;

    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    const features = `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`;

    const portalUrl = `${window.location.origin}/?sld-portal=1`;
    const win = window.open(portalUrl, '_blank', features);

    if (!win) { onClose(); return; }

    // Poll for the portal root div (new window needs to load the app JS first)
    let pollTimer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const checkReady = () => {
      attempts++;
      try {
        const div = win.document.getElementById('sld-portal-root');
        if (div) {
          win.document.title = title;
          containerRef.current = div as HTMLDivElement;
          setMounted(true);
          return;
        }
      } catch { /* cross-origin or not ready yet */ }
      if (attempts < 100) {
        pollTimer = setTimeout(checkReady, 100);
      }
    };
    pollTimer = setTimeout(checkReady, 300);

    const closedTimer = setInterval(() => {
      if (win.closed) { clearInterval(closedTimer); onClose(); }
    }, 500);

    return () => {
      clearTimeout(pollTimer);
      clearInterval(closedTimer);
      if (!win.closed) win.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Electron mode renders nothing here — the child BrowserWindow is independent
  if (isElectron) return null;
  if (!mounted || !containerRef.current) return null;
  return createPortal(children, containerRef.current);
}
